import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronDown, ChevronRight, Cloud, IndianRupee, Search, Server, Wallet } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BillingFiltersBar } from '../../../components/MasterAdmin/BillingAnalytics/BillingFiltersBar';
import {
  ShareBar,
  SortHeader,
  chartTooltipStyle,
  formatCompact,
  formatCurrency,
  formatDateShort,
  paletteColor,
  tdClass,
  thClass,
} from '../../../components/MasterAdmin/BillingAnalytics/billingUi';
import InsightsPageShell from '../../../components/MasterAdmin/insights/InsightsPageShell';
import type { MetricInfoDefinition } from '../../../components/MasterAdmin/ui/MetricInfoPopover';
import OperationsStatCard from '../../../components/MasterAdmin/operations/OperationsStatCard';
import { OpsChartTitle } from '../../../components/MasterAdmin/operations/OpsMetricLabels';
import { opsChartCard, opsFilterClass, opsMetricGridClass } from '../../../components/MasterAdmin/operations/operationsUi';
import { getBillingResources, getResourceHistory, getTimeRangeDates } from '../../../services/MasterAdmin/billingAnalyticsService';

const PAGE_INFO: MetricInfoDefinition = {
  definition: 'Highest-cost GCP resources (instances, buckets, APIs, databases) with parent service, usage, and daily spend on expand.',
  sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['resource.name', 'sku.description', 'cost'] }],
};

type ResourceRow = {
  id: string;
  name: string;
  historyKey: string;
  service: string;
  netCost: number;
  grossCost: number;
  credits: number;
  currency: string;
  usage: string;
  lastActive: string;
  lastActiveRaw: string | null;
  share: number;
  color: string;
};

const BillingResourcesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const timeRange = searchParams.get('timeRange') || 'This Month';
  const projectId = searchParams.get('projectId') || 'vedika-health';
  const customStart = searchParams.get('startDate') || undefined;
  const customEnd = searchParams.get('endDate') || undefined;
  const serviceId = searchParams.get('serviceId') || undefined;
  const resourceName = searchParams.get('resourceName') || undefined;

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourcesData, setResourcesData] = useState<ResourceRow[]>([]);
  const [historyData, setHistoryData] = useState<Record<string, Array<{ date: string; rawDate: string; netCost: number }>>>({});
  const [loadingHistory, setLoadingHistory] = useState<Record<string, boolean>>({});
  const [sortField, setSortField] = useState('netCost');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const fetchResources = async () => {
      if (!projectId) return;
      try {
        setIsLoading(true);
        setError(null);
        const { start, end } = getTimeRangeDates(timeRange, customStart, customEnd);
        const data = await getBillingResources(start, end, projectId, 50, serviceId, resourceName);
        const totalNet = (data || []).reduce((sum: number, r: any) => sum + Number(r.netCost || 0), 0);
        setResourcesData(
          (data || []).map((r: any, idx: number) => {
            const name = r.resourceName || r.resourceGlobalName || r.skuDescription || 'Unknown resource';
            return {
              id: `${name}-${idx}`,
              name,
              historyKey: r.resourceName || r.resourceGlobalName || name,
              service: r.serviceDescription || 'Unknown service',
              netCost: Number(r.netCost || 0),
              grossCost: Number(r.grossCost || 0),
              credits: Math.abs(Number(r.credits || 0)),
              currency: r.currency || 'INR',
              usage: r.usage || 'N/A',
              lastActive: r.latestUsage ? formatDateShort(r.latestUsage) : 'N/A',
              lastActiveRaw: r.latestUsage || null,
              share: totalNet > 0 ? (Number(r.netCost || 0) / totalNet) * 100 : 0,
              color: paletteColor(idx),
            };
          })
        );
      } catch (err) {
        console.error('Failed to fetch resources data:', err);
        setError('Unable to load resource billing. Please retry.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResources();
  }, [timeRange, projectId, customStart, customEnd, serviceId, resourceName]);

  const toggleExpand = async (id: string, historyKey: string) => {
    const isExpanding = expandedId !== id;
    setExpandedId(isExpanding ? id : null);
    if (!isExpanding || historyData[id]) return;
    try {
      setLoadingHistory((prev) => ({ ...prev, [id]: true }));
      const { start, end } = getTimeRangeDates(timeRange, customStart, customEnd);
      const data = await getResourceHistory(historyKey, start, end, projectId);
      setHistoryData((prev) => ({
        ...prev,
        [id]: (data || []).map((t: any) => ({
          date: t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '',
          rawDate: t.date,
          netCost: Number(t.netCost || 0),
        })),
      }));
    } catch (err) {
      console.error('Failed to fetch history for resource:', id, err);
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q
      ? resourcesData.filter((r) => r.name.toLowerCase().includes(q) || r.service.toLowerCase().includes(q))
      : resourcesData;
    return [...rows].sort((a, b) => {
      let aVal: any = a[sortField as keyof ResourceRow];
      let bVal: any = b[sortField as keyof ResourceRow];
      if (sortField === 'lastActive') {
        aVal = a.lastActiveRaw ? new Date(a.lastActiveRaw).getTime() : 0;
        bVal = b.lastActiveRaw ? new Date(b.lastActiveRaw).getTime() : 0;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [resourcesData, query, sortField, sortOrder]);

  const totals = useMemo(() => {
    const net = resourcesData.reduce((s, r) => s + r.netCost, 0);
    const gross = resourcesData.reduce((s, r) => s + r.grossCost, 0);
    const credits = resourcesData.reduce((s, r) => s + r.credits, 0);
    const top = [...resourcesData].sort((a, b) => b.netCost - a.netCost)[0];
    const uniqueServices = new Set(resourcesData.map((r) => r.service)).size;
    return { net, gross, credits, top, uniqueServices, count: resourcesData.length, currency: resourcesData[0]?.currency || 'INR' };
  }, [resourcesData]);

  const serviceMix = useMemo(() => {
    const map = new Map<string, number>();
    resourcesData.forEach((r) => map.set(r.service, (map.get(r.service) || 0) + r.netCost));
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, netCost], idx) => ({
        name,
        netCost,
        share: totals.net > 0 ? (netCost / totals.net) * 100 : 0,
        color: paletteColor(idx),
      }));
  }, [resourcesData, totals.net]);

  const barData = useMemo(
    () =>
      [...resourcesData]
        .sort((a, b) => b.netCost - a.netCost)
        .slice(0, 8)
        .map((r) => ({
          name: r.name.length > 28 ? `${r.name.slice(0, 26)}…` : r.name,
          fullName: r.name,
          netCost: r.netCost,
          fill: r.color,
        })),
    [resourcesData]
  );

  return (
    <InsightsPageShell
      title="Billable Resources"
      description="The specific cloud resources driving cost, grouped by parent service."
      info={PAGE_INFO}
    >
      <BillingFiltersBar />

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900/40 dark:bg-red-900/20">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            <OperationsStatCard label="Listed net cost" infoKey="totalCostNet" value={formatCurrency(totals.net, totals.currency)} sub="Top resources in this view" icon={IndianRupee} />
            <OperationsStatCard label="Gross cost" infoKey="totalGrossCost" value={formatCurrency(totals.gross, totals.currency)} sub="Before credits" icon={Wallet} />
            <OperationsStatCard label="Resources" infoKey="uniqueResources" value={String(totals.count)} sub="Unique billable items" icon={Server} />
            <OperationsStatCard
              label="Parent services"
              infoKey="totalServicesUsed"
              value={String(totals.uniqueServices)}
              sub={totals.top ? `Top: ${totals.top.share.toFixed(0)}% one resource` : 'No resources'}
              icon={Cloud}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            <div className={`${opsChartCard} xl:col-span-3`}>
              <OpsChartTitle title="Highest-cost resources" infoKey="topResourcesTable" />
              {barData.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">No resources billed in this period</p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatCompact} />
                      <YAxis type="category" dataKey="name" width={168} tick={{ fontSize: 10 }} />
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value: number) => [formatCurrency(value, totals.currency), 'Net cost']}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                      />
                      <Bar dataKey="netCost" radius={[0, 6, 6, 0]} maxBarSize={22}>
                        {barData.map((entry) => (
                          <Cell key={entry.fullName} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className={`${opsChartCard} xl:col-span-2`}>
              <OpsChartTitle title="Resources by parent service" infoKey="costDistribution" />
              {serviceMix.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">No resource spend in this period</p>
              ) : (
                <>
                  <div className="relative h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={serviceMix} cx="50%" cy="50%" innerRadius={52} outerRadius={78} paddingAngle={2} dataKey="netCost" nameKey="name" stroke="none">
                          {serviceMix.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip {...chartTooltipStyle} formatter={(value: number, name: string) => [formatCurrency(value, totals.currency), name]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Net</span>
                      <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{formatCompact(totals.net)}</span>
                    </div>
                  </div>
                  <div className="mt-2 max-h-36 space-y-1.5 overflow-y-auto">
                    {serviceMix.map((item) => (
                      <div key={item.name} className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="truncate text-xs font-medium text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="shrink-0 text-xs font-semibold tabular-nums">{item.share.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Resource ledger</p>
                <p className="mt-0.5 text-xs text-gray-500">Click a row to see daily spend for that resource.</p>
              </div>
              <label className={`${opsFilterClass} flex max-w-xs items-center gap-2`}>
                <Search className="h-4 w-4 shrink-0 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search resource or service"
                  className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
                />
              </label>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/80">
                  <tr>
                    <th className={`${thClass} w-10`} />
                    <th className={`${thClass} text-left`}>
                      <SortHeader label="Resource" field="name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                    </th>
                    <th className={`${thClass} text-left`}>
                      <SortHeader label="Service" field="service" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Gross" field="grossCost" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Credits" field="credits" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Net cost" field="netCost" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                    <th className={`${thClass} text-left`}>Usage</th>
                    <th className={`${thClass} text-left`}>
                      <SortHeader label="Share" field="share" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                    </th>
                    <th className={`${thClass} text-right`}>
                      <div className="flex justify-end">
                        <SortHeader label="Last active" field="lastActive" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} align="right" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                        {query ? 'No resources match your search' : 'No resources billed in this period'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((resource) => {
                      const isExpanded = expandedId === resource.id;
                      const history = historyData[resource.id] || [];
                      const peak = history.reduce<(typeof history)[0] | null>((best, row) => (!best || row.netCost > best.netCost ? row : best), null);
                      return (
                        <React.Fragment key={resource.id}>
                          <tr
                            className={`cursor-pointer bg-white hover:bg-gray-50 dark:bg-gray-900/40 dark:hover:bg-gray-800/60 ${isExpanded ? 'bg-primary-50/40 dark:bg-primary-950/20' : ''}`}
                            onClick={() => toggleExpand(resource.id, resource.historyKey)}
                          >
                            <td className={tdClass}>
                              {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            </td>
                            <td className={`${tdClass} max-w-[280px]`}>
                              <p className="truncate font-mono text-xs font-semibold text-gray-900 dark:text-white" title={resource.name}>
                                {resource.name}
                              </p>
                            </td>
                            <td className={tdClass}>
                              <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {resource.service}
                              </span>
                            </td>
                            <td className={`${tdClass} text-right font-mono text-gray-500`}>{formatCurrency(resource.grossCost, resource.currency)}</td>
                            <td className={`${tdClass} text-right font-mono text-emerald-600 dark:text-emerald-400`}>
                              {formatCurrency(resource.credits, resource.currency)}
                            </td>
                            <td className={`${tdClass} text-right font-mono font-semibold`}>{formatCurrency(resource.netCost, resource.currency)}</td>
                            <td className={`${tdClass} max-w-[160px] truncate text-xs text-gray-500`} title={resource.usage}>
                              {resource.usage}
                            </td>
                            <td className={tdClass}>
                              <ShareBar share={resource.share} color={resource.color} />
                            </td>
                            <td className={`${tdClass} text-right text-xs text-gray-500`}>{resource.lastActive}</td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50/80 dark:bg-gray-900/30">
                              <td colSpan={9} className="px-4 py-4">
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                                  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Resource snapshot</p>
                                    <dl className="mt-3 space-y-2 text-sm">
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Service</dt>
                                        <dd className="font-semibold">{resource.service}</dd>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Share of listed</dt>
                                        <dd className="font-semibold">{resource.share.toFixed(1)}%</dd>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Last active</dt>
                                        <dd className="font-semibold">{resource.lastActive}</dd>
                                      </div>
                                      <div className="flex justify-between gap-4">
                                        <dt className="text-gray-500">Peak day</dt>
                                        <dd className="font-semibold">
                                          {peak ? `${formatDateShort(peak.rawDate)} · ${formatCurrency(peak.netCost, resource.currency)}` : '—'}
                                        </dd>
                                      </div>
                                    </dl>
                                  </div>
                                  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
                                    <p className="mb-3 text-xs font-semibold text-gray-700 dark:text-gray-200">Daily net cost</p>
                                    {loadingHistory[resource.id] ? (
                                      <div className="h-48 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700" />
                                    ) : history.length === 0 ? (
                                      <p className="py-16 text-center text-sm text-gray-500">No daily history for this resource</p>
                                    ) : (
                                      <div className="h-48">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <AreaChart data={history}>
                                            <defs>
                                              <linearGradient id={`res-${resource.id}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={resource.color} stopOpacity={0.35} />
                                                <stop offset="100%" stopColor={resource.color} stopOpacity={0} />
                                              </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompact} width={48} />
                                            <Tooltip
                                              {...chartTooltipStyle}
                                              formatter={(value: number) => [formatCurrency(value, resource.currency), 'Net cost']}
                                              labelFormatter={(_, payload) =>
                                                payload?.[0]?.payload?.rawDate ? formatDateShort(payload[0].payload.rawDate) : ''
                                              }
                                            />
                                            <Area type="monotone" dataKey="netCost" stroke={resource.color} fill={`url(#res-${resource.id})`} strokeWidth={2} />
                                          </AreaChart>
                                        </ResponsiveContainer>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
                {filtered.length > 0 && (
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold dark:border-gray-700 dark:bg-gray-800/80">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500">
                        Total (listed)
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(totals.gross, totals.currency)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(totals.credits, totals.currency)}</td>
                      <td className="px-4 py-3 text-right font-mono text-primary-600">{formatCurrency(totals.net, totals.currency)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
    </InsightsPageShell>
  );
};

export default BillingResourcesPage;
